import type { NodeId, StickyZoneId } from 'balus-common';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { DisplayOrderTree } from './DisplayOrderTree';

type ContainedElement<Id> = { id: Id; rect: Rect };

/**
 * 要素の矩形の包含関係からDisplayOrderTreeを構築する。
 *
 * ゾーンへの所属は座標ではなくDisplayOrderTree上の親子関係として保持されるため、
 * 所属先を幾何的に導出する。内包判定(ゾーン矩形が要素矩形を完全に含む)は
 * D&D時のドロップ判定(StickyModel.findDropTargetZones)と同じだが、複数のゾーンが
 * 内包する場合の親の選択基準は異なる: D&Dは表示順で最前面のゾーンを選ぶのに対し、
 * ここでは表示順がまだ存在しない(これから構築する)ため、最も内側とみなせる
 * 最小面積のゾーンを親として選ぶ。
 */
export function buildDisplayOrderTreeByContainment(
  nodes: ContainedElement<NodeId>[],
  zones: ContainedElement<StickyZoneId>[]
): DisplayOrderTree {
  // rectを完全に内包する最小面積のゾーンを返す。同一矩形は親子にしない(循環防止)
  const findParentZoneId = (rect: Rect, excludeZoneId?: string): StickyZoneId | null => {
    let parentZoneId: StickyZoneId | null = null;
    let parentArea = Infinity;
    for (const zone of zones) {
      if (zone.id === excludeZoneId) continue;
      if (zone.rect.isEqual(rect) || !zone.rect.includeRect(rect)) continue;
      const area = zone.rect.size.width * zone.rect.size.height;
      if (area < parentArea) {
        parentZoneId = zone.id;
        parentArea = area;
      }
    }
    return parentZoneId;
  };

  const zoneParentMap = new Map(zones.map((zone) => [zone.id, findParentZoneId(zone.rect, zone.id)] as const));

  let orderTree = new DisplayOrderTree().addZoneList(
    zones.filter((zone) => zoneParentMap.get(zone.id) === null).map((zone) => zone.id)
  );

  // 親ゾーンが先にツリーへ追加されている必要があるため、面積の大きい順(親→子)に追加する
  const areaOf = (zone: ContainedElement<StickyZoneId>): number => zone.rect.size.width * zone.rect.size.height;
  const childZones = zones.filter((zone) => zoneParentMap.get(zone.id) !== null).sort((a, b) => areaOf(b) - areaOf(a));
  for (const zone of childZones) {
    orderTree = orderTree.addZoneToZone(zone.id, zoneParentMap.get(zone.id)!);
  }

  for (const node of nodes) {
    const parentZoneId = findParentZoneId(node.rect);
    orderTree = parentZoneId ? orderTree.addNodeToZone(node.id, parentZoneId) : orderTree.addNode(node.id);
  }

  return orderTree;
}
